import dayjs, { Dayjs } from "dayjs";
import { useState, useEffect } from "react";
import { RadioGroup } from "@mui/material";
import { FaUser, FaUsers, FaSortAmountDown, FaSortAmountUp } from "react-icons/fa";
import CustomRadioButton from "components/RadioButton";
import { Drawer } from "components/Drawer";
import { DateRangePicker } from "components/DateRangePicker";
import { TpMultiSelectAutocomplete } from "components/TpMultiSelectAutocomplete";
import { StyledToggleButtonGroup } from "components/StyledToggleButtonGroup";
import { FilterSection, InputSection, CustomDateSection, SectionTitle } from "./styles";
import { ReasonsMultiSelectAutocomplete } from "../../pages/Complaints/components/ReasonsMultiSelectAutocomplete";

type DateRange<T> = [T | null, T | null];

interface IComplaintsFilterProps {
  open: boolean;
  setOpen: (x: boolean) => void;
  onChangeParams: (x: any) => void;
  loading: boolean;
  initialParams?: {
    startDate?: string;
    endDate?: string;
    transshipPointsIds?: number[];
    groupBy?: string;
    order?: string;
    reasons?: string[];
  };
}

const today = new Date();
const last7Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
const last15Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 15);
const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

const formatDate = (date: Date) => dayjs(date).format("YYYY-MM-DD");

const initialEntries = [
  { id: 1, name: "Hoje", query: formatDate(today) },
  { id: 2, name: "Último mês", query: formatDate(lastMonth) },
  { id: 3, name: "Últimos 15 dias", query: formatDate(last15Days) },
  { id: 4, name: "Últimos 7 dias", query: formatDate(last7Days) },
  { id: 5, name: "Período customizado", query: "custom" },
];

const groupByOptions = [
  { value: "driver", label: "Motorista", icon: <FaUser size={14} /> },
  { value: "team", label: "Time", icon: <FaUsers size={18} /> },
];

const orderOptions = [
  { value: "asc", label: "Quebra (%)", icon: <FaSortAmountDown size={14} /> },
  { value: "desc", label: "Quebra (%)", icon: <FaSortAmountUp size={14} /> },
];

export function FilterComplaints({ open, setOpen, onChangeParams, loading, initialParams }: IComplaintsFilterProps) {
  // Determina qual período predefinido está sendo usado
  const getInitialSelectedDate = () => {
    if (!initialParams?.startDate || !initialParams?.endDate) {
      return formatDate(today); // Default para "Hoje"
    }

    const startDate = initialParams.startDate;
    const endDate = initialParams.endDate;
    const todayFormatted = formatDate(today);

    // Se é um período customizado (startDate != endDate e endDate != hoje)
    if (startDate !== endDate && endDate !== todayFormatted) {
      return "custom";
    }

    // Verifica se corresponde a algum período predefinido
    const matchingEntry = initialEntries.find(entry => entry.query === startDate);
    return matchingEntry ? matchingEntry.query : "custom";
  };

  // Determina o dateRange inicial se for customizado
  const getInitialDateRange = (): DateRange<Dayjs> => {
    if (!initialParams?.startDate || !initialParams?.endDate || getInitialSelectedDate() !== "custom") {
      return [null, null];
    }

    try {
      const startDayjs = dayjs(initialParams.startDate);
      const endDayjs = dayjs(initialParams.endDate);
      return [startDayjs, endDayjs];
    } catch {
      return [null, null];
    }
  };

  const [selectedDate, setSelectedDate] = useState<string>(() => getInitialSelectedDate());
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(() => getInitialDateRange());
  const [selectedTransshipPoints, setSelectedTransshipPoints] = useState<number[]>(
    initialParams?.transshipPointsIds || []
  );
  const [selectedGroupBy, setSelectedGroupBy] = useState<string>(
    initialParams?.groupBy || "driver"
  );
  const [selectedOrder, setSelectedOrder] = useState<string>(
    initialParams?.order || "desc"
  );
  const [selectedReasons, setSelectedReasons] = useState<string[]>(
    initialParams?.reasons || []
  );
  const [pendingParams, setPendingParams] = useState<any>(initialParams || {});

  // Função para determinar qual período predefinido está sendo usado
  const getUpdatedSelectedDate = () => {
    if (!initialParams?.startDate || !initialParams?.endDate) {
      return formatDate(today); // Default para "Hoje"
    }

    const startDate = initialParams.startDate;
    const endDate = initialParams.endDate;
    const todayFormatted = formatDate(today);

    // Se é um período customizado (startDate != endDate e endDate != hoje)
    if (startDate !== endDate && endDate !== todayFormatted) {
      return "custom";
    }

    // Verifica se corresponde a algum período predefinido
    const matchingEntry = initialEntries.find(entry => entry.query === startDate);
    return matchingEntry ? matchingEntry.query : "custom";
  };

  // Função para determinar o dateRange se for customizado
  const getUpdatedDateRange = (): DateRange<Dayjs> => {
    if (!initialParams?.startDate || !initialParams?.endDate) {
      return [null, null];
    }

    try {
      const startDayjs = dayjs(initialParams.startDate);
      const endDayjs = dayjs(initialParams.endDate);
      return [startDayjs, endDayjs];
    } catch {
      return [null, null];
    }
  };

  // Função para sincronizar todos os estados internos
  const syncStatesFromParams = () => {
    if (!initialParams) return;

    const newSelectedDate = getUpdatedSelectedDate();
    setSelectedDate(newSelectedDate);
    setDateRange(getUpdatedDateRange());
    setSelectedTransshipPoints(initialParams.transshipPointsIds || []);
    setSelectedGroupBy(initialParams.groupBy || "driver");
    setSelectedOrder(initialParams.order || "desc");
    setSelectedReasons(initialParams.reasons || []);
    setPendingParams(initialParams);
  };

  // Sincroniza estados internos quando initialParams mudam
  useEffect(() => {
    syncStatesFromParams();
  }, [initialParams]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);

    if (newDate !== "custom") {
      setDateRange([null, null]);
      const todayDate = formatDate(today);
      setPendingParams((prev: any) => ({
        ...prev,
        startDate: newDate,
        endDate: todayDate, // endDate sempre é hoje para períodos predefinidos
        page: 1
      }));
    }
  };

  const handleDateRangeChange = (newDateRange: DateRange<Dayjs>) => {
    setDateRange(newDateRange);

    if (newDateRange[0] && newDateRange[1]) {
      setPendingParams((prev: any) => ({
        ...prev,
        startDate: newDateRange[0]!.format("YYYY-MM-DD"),
        endDate: newDateRange[1]!.format("YYYY-MM-DD"),
        page: 1
      }));
    }
  };

  const handleTransshipPointsChange = (newTransshipPoints: number[]) => {
    setSelectedTransshipPoints(newTransshipPoints);
    setPendingParams((prev: any) => ({
      ...prev,
      transshipPointsIds: newTransshipPoints,
      page: 1
    }));
  };

  const handleGroupByChange = (newGroupBy: string) => {
    setSelectedGroupBy(newGroupBy);
    setPendingParams((prev: any) => ({
      ...prev,
      groupBy: newGroupBy,
      page: 1
    }));
  };

  const handleOrderChange = (newOrder: string) => {
    setSelectedOrder(newOrder);
    setPendingParams((prev: any) => ({
      ...prev,
      order: newOrder,
      page: 1
    }));
  };

  const handleReasonsChange = (newReasons: string[]) => {
    setSelectedReasons(newReasons);
    setPendingParams((prev: any) => ({
      ...prev,
      reasons: newReasons,
      page: 1
    }));
  };

  const handleApplyFilters = () => {
    onChangeParams(pendingParams);
    setOpen(false);
  };

  const handleClearFilters = () => {
    const todayDate = formatDate(today);
    const clearedParams = {
      startDate: todayDate,
      endDate: todayDate,
      transshipPointsIds: [],
      groupBy: "driver",
      order: "desc",
      reasons: [],
      page: 1
    };

    setSelectedDate(todayDate);
    setDateRange([null, null]);
    setSelectedTransshipPoints([]);
    setSelectedGroupBy("driver");
    setSelectedOrder("desc");
    setSelectedReasons([]);
    setPendingParams(clearedParams);
  };

  return (
    <Drawer
      open={open}
      onClose={() => setOpen(false)}
      title="Filtros"
      onApply={handleApplyFilters}
      onClear={handleClearFilters}
      buttonsDisabled={loading}
    >
      <FilterSection>
        <SectionTitle>Período</SectionTitle>
        <RadioGroup value={selectedDate} onChange={handleRadioChange}>
          {initialEntries.map((item) => (
            <CustomRadioButton
              key={item.id}
              text={item.name}
              value={item.query}
              checked={selectedDate === item.query}
              handleChange={handleRadioChange}
              size="small"
            />
          ))}
        </RadioGroup>

        {selectedDate === "custom" && (
          <CustomDateSection>
            <SectionTitle>Período Customizado</SectionTitle>
            <DateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              label="Selecione o período"
              placeholder="Escolha as datas"
              maxDays={30}
            />
          </CustomDateSection>
        )}
      </FilterSection>

      <InputSection>
        <SectionTitle>Pontos de transbordo</SectionTitle>
        <TpMultiSelectAutocomplete
          value={selectedTransshipPoints}
          onChange={handleTransshipPointsChange}
          disabled={loading}
        />
      </InputSection>

      <InputSection>
        <SectionTitle>Motivos</SectionTitle>
        <ReasonsMultiSelectAutocomplete
          value={selectedReasons}
          onChange={handleReasonsChange}
          disabled={loading}
          placeholder="Selecione os motivos"
        />
      </InputSection>

      <InputSection>
        <SectionTitle>Visualização</SectionTitle>
        <StyledToggleButtonGroup
          value={selectedGroupBy}
          onChange={(_, newValue) => newValue && handleGroupByChange(newValue)}
          disabled={loading}
          options={groupByOptions}
        />
      </InputSection>

      <InputSection>
        <SectionTitle>Ordenação</SectionTitle>
        <StyledToggleButtonGroup
          value={selectedOrder}
          onChange={(_, newValue) => newValue && handleOrderChange(newValue)}
          disabled={loading}
          options={orderOptions}
        />
      </InputSection>
    </Drawer>
  );
}
